// Client-side HLS → MP4 download driver.
//
// Sibling of fmp4-merge.js (box surgery + playlist parsers, so node tests run
// without DOM) and vtt-to-srt.js (subtitle conversion). This file owns I/O:
//
//   1. GET /api/hosted/download-info?type=movie&id=…
//        → { downloadable, masterUrl, variants[], subtitles[], title }
//   2. mergeInitSegments(video init, audio init) → dual-track header
//   3. interleave [video i][audio i] fragments, each stripped to [moof|mdat];
//      audio fragments' tfhd retagged 1→2 (they were authored as track 1)
//   4. stream to a real file (File System Access API) or Blob fallback
//   5. optionally fetch a WebVTT track and save it converted to .srt
//
// Sequential segment fetches keep memory flat and make progress honest
// (segment i of N). Everything is abortable via AbortSignal; a failed segment
// fetch retries once before giving up (CDN hiccups are common; a truncated
// package is fatal).

import {
  mergeInitSegments,
  stripToFragment,
  retagFragmentTrackId,
  parseMediaPlaylist,
  parseMasterPlaylist,
  readTkhdDimensions,
  safeFilename,
} from "./fmp4-merge.js";
import { vttToSrt, subtitleFilename } from "./vtt-to-srt.js";
import { openStreamDownload, streamDownloadSupported, StreamDownloadUnavailable } from "./stream-download.js";

const SEGMENT_RETRIES = 1;

async function fetchRetry(url, { as = "text", signal, retries = SEGMENT_RETRIES } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const res = await fetch(url, { signal, redirect: "follow" });
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
      if (as === "bytes") return new Uint8Array(await res.arrayBuffer());
      return await res.text();
    } catch (e) {
      if (signal?.aborted || e?.name === "AbortError") throw e;
      lastErr = e;
      if (attempt < retries) await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
  throw lastErr;
}

/**
 * Probe whether a title has a downloadable package, and what the dialog
 * should offer. Returns { downloadable, masterUrl, title, variants,
 * subtitles } — downloadable:false when the title's links are all host
 * embeds (nothing the browser can save).
 */
export async function probeDownloadable(type, tmdbId, signal) {
  const params = new URLSearchParams({ type: type === "episode" ? "episode" : "movie", id: String(tmdbId) });
  const res = await fetch(`/api/hosted/download-info?${params}`, { signal });
  if (!res.ok) return { downloadable: false, variants: [], subtitles: [] };
  const data = await res.json().catch(() => ({}));
  return {
    downloadable: data.downloadable === true,
    // "cdn"  — our own Cloudflare package: the browser fetches the fMP4
    //          fragments directly and stitches them (downloadHlsToMp4).
    // "relay" — LuluStream: the signed playlists only answer to our relay, so
    //          every request goes through /api/hosted/media and the MPEG-TS
    //          segments are transmuxed to MP4 in the browser.
    source: data.source === "relay" ? "relay" : "cdn",
    muxed: data.muxed === true, // one combined A/V track — no audio picker
    audio: data.audio || null,
    masterUrl: data.masterUrl || null,
    // A link that re-mints the source's token per request, so it can be copied
    // and kept. masterUrl carries the source's signed URL and dies with it.
    permanentUrl: data.permanentUrl || null,
    // Same content, named `.m3u8`: what a re-hosting service wants. See the
    // note on stableImportFilename in netlify/functions/lib/stable-stream.mjs.
    importUrl: data.importUrl || null,
    title: data.title || "",
    variants: Array.isArray(data.variants) ? data.variants : [],
    subtitles: Array.isArray(data.subtitles) ? data.subtitles : [],
  };
}

/**
 * Fetch a subtitle track and hand it to the caller as .srt text. The library
 * stores WebVTT; standalone players want SubRip. Throws on non-VTT payloads
 * (the converter guards) so the UI can surface an honest error.
 */
export async function downloadSubtitleSrt(track, signal) {
  const res = await fetch(track.url, { signal });
  if (!res.ok) throw new Error(`Subtitles unavailable (HTTP ${res.status})`);
  const srt = vttToSrt(await res.text());
  return { filename: subtitleFilename(track.label || track.lang), srt };
}

/**
 * Download a RELAYED stream (LuluStream) as one .mp4.
 *
 * Their CDN answers only to the luluvdo Referer + desktop UA the browser
 * cannot send, so the playlists and every segment are fetched through
 * /api/hosted/media (the relay that already carries playback). The stream is
 * MPEG-TS with muxed audio, so each segment is transmuxed to fMP4 by mux.js
 * and streamed straight to the save handle — same output shape as the fMP4
 * packages, just a different container on the way in.
 *
 * Returns { filename, bytes, segmentCount } and reports the same progress
 * phases as downloadHlsToMp4.
 */
export async function downloadRelayTsToMp4({
  masterUrl,
  title,
  variantIndex = 0,
  signal,
  onProgress = () => {},
  savePicker = defaultSavePicker,
}) {
  if (!masterUrl) throw new Error("No downloadable source for this title");
  // The relay URL is site-relative; resolve it the way a browser would so the
  // playlist parser can turn child URIs into absolute ones.
  const base = typeof location !== "undefined" && location.href ? new URL(masterUrl, location.href).href : masterUrl;
  const report = (phase, extra = {}) => onProgress({ phase, filename, ...extra });
  let filename = safeFilename(title || "movie");

  const master = parseMasterPlaylist(await fetchRetry(base, { signal }), base);
  const variant = master.variants[variantIndex] || master.variants[0];
  if (!variant?.uri) throw new Error("Package has no video playlist");
  report("init");
  const media = parseMediaPlaylist(await fetchRetry(variant.uri, { signal }), variant.uri);
  const segments = media.segments;
  if (!segments.length) throw new Error("Video playlist has no segments");
  if (variant.height) filename = safeFilename(`${title || "movie"} ${variant.height}p`);

  // mux.js renders each TS segment into "combined" fMP4 fragments (init once,
  // then moof+mdat). Loaded lazily — it dwarfs the rest of this module and is
  // only ever needed for a relayed download.
  // Explicit file path: the package has no `exports` map, and a bare
  // "mux.js/lib/mp4" directory import is rejected by node's ESM resolver.
  const mod = await import("mux.js/lib/mp4/index.js");
  const Mux = mod?.Transmuxer ? mod : mod?.default || {};
  const Transmuxer = Mux.Transmuxer;
  if (!Transmuxer) throw new Error("TS remuxer unavailable");

  const out = await savePicker(filename, signal);
  const write = out.write; // FileSystemWritableFileStream or null
  const chunks = write ? null : [];
  let bytes = 0;
  const emit = (buf) => {
    bytes += buf.byteLength ?? buf.size ?? 0;
    if (write) return write.write(buf);
    chunks.push(buf);
    return Promise.resolve();
  };

  // The transmuxer hands out fragments synchronously from push(); writes are
  // async, so keep them in one promise chain to preserve byte order.
  let queue = Promise.resolve();
  let wroteInit = false;
  const transmuxer = new Transmuxer({ remux: true });
  transmuxer.on("data", (fragment) => {
    if (!wroteInit && fragment.initSegment) {
      wroteInit = true;
      const init = fragment.initSegment;
      queue = queue.then(() => emit(init));
    }
    if (fragment.data) {
      const data = fragment.data;
      queue = queue.then(() => emit(data));
    }
  });

  try {
    // Small read-ahead window: the segments stay ordered (the transmuxer
    // needs them in order) while a few fetches overlap network latency.
    const PREFETCH = 3;
    const inflight = [];
    const start = (i) => fetchRetry(segments[i], { as: "bytes", signal });
    for (let i = 0; i < Math.min(PREFETCH, segments.length); i += 1) inflight.push(start(i));

    let done = 0;
    for (let i = 0; i < segments.length; i += 1) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const buf = await inflight[i];
      const next = i + PREFETCH;
      if (next < segments.length) inflight.push(start(next));
      transmuxer.push(buf);
      // mux.js buffers TS until it is flushed — without this, nothing is
      // emitted until the very end (and the file grows only once). Flushing
      // per segment emits that segment's fragments; the init segment arrives
      // with the first one and is written exactly once.
      transmuxer.flush();
      done += 1;
      report("segments", {
        segmentIndex: done,
        segmentCount: segments.length,
        percent: done / segments.length,
        bytes,
      });
    }
    transmuxer.flush();
    report("finalizing", { percent: 1, segmentIndex: segments.length, segmentCount: segments.length, bytes });
    await queue; // every fragment is on disk before the handle closes
    if (write) await write.close();
    else triggerBlobSave(new Blob(chunks, { type: "video/mp4" }), filename);
  } catch (e) {
    try {
      await write?.abort?.();
    } catch {}
    throw e;
  }

  return { filename, bytes, segmentCount: segments.length };
}

/**
 * Run the video download.
 *   variantIndex — which entry of info.variants to fetch (default: first)
 *   audioIndex   — which audio rendition (default: the package's DEFAULT=YES,
 *                  else the first)
 * Returns { filename, bytes, segmentCount }.
 *
 * onProgress receives { phase, percent, segmentIndex, segmentCount, bytes,
 * filename } — phase: "init" | "segments" | "finalizing".
 */
export async function downloadHlsToMp4({
  masterUrl,
  title,
  variantIndex = 0,
  audioIndex = null,
  signal,
  onProgress = () => {},
  savePicker = defaultSavePicker,
}) {
  if (!masterUrl) throw new Error("No downloadable source for this title");

  const report = (phase, extra = {}) => onProgress({ phase, filename, ...extra });
  let filename = safeFilename(title || "movie"); // height-tagged once known

  const master = parseMasterPlaylist(await fetchRetry(masterUrl, { signal }), masterUrl);
  const variant = master.variants[variantIndex] || master.variants[0];
  if (!variant?.uri) throw new Error("Package has no video playlist");

  // Which audio rendition? The package's DEFAULT=YES is what the player picks;
  // audioIndex overrides (the dialog lists every rendition with its language).
  const renditions = master.audioRenditions;
  const chosen =
    audioIndex != null && renditions[audioIndex]
      ? renditions[audioIndex]
      : renditions.find((r) => r.default) || renditions[0] || null;

  report("init");
  const [videoText, audioText] = await Promise.all([
    fetchRetry(variant.uri, { signal }),
    chosen ? fetchRetry(chosen.url, { signal }) : Promise.resolve(null),
  ]);
  const video = parseMediaPlaylist(videoText, variant.uri);
  const audio = audioText ? parseMediaPlaylist(audioText, chosen.url) : null;
  if (!video.segments.length) throw new Error("Video playlist has no segments");

  const [videoInit, audioInit] = await Promise.all([
    fetchRetry(video.initUrl, { as: "bytes", signal }),
    audio && audio.initUrl ? fetchRetry(audio.initUrl, { as: "bytes", signal }) : Promise.resolve(null),
  ]);
  const header =
    videoInit && audioInit
      ? mergeInitSegments(videoInit, audioInit)
      : videoInit; // video-only package: ftyp+moov used verbatim

  // Tag the quality into the filename once the height is known. Our packages'
  // master playlists carry no RESOLUTION attribute, so fall back to reading
  // the real pixel height from the init segment's tkhd (same source the
  // server-side probe uses) — keeps file and dialog label consistent.
  let height = variant.height;
  if (!height && videoInit) height = readTkhdDimensions(videoInit)?.height;
  if (height) filename = safeFilename(`${title || "movie"} ${height}p`);

  // Audio fragments were authored as track 1 in their own init; the merged
  // movie retags audio to track 2, so every audio fragment must be retagged
  // too (a 4-byte tfhd patch — see retagFragmentTrackId). Video fragments
  // pass through untouched.
  const prepareFragment = (buf, isAudio) => {
    const frag = stripToFragment(buf);
    return isAudio && audioInit ? retagFragmentTrackId(frag, 2) : frag;
  };

  const out = await savePicker(filename, signal);
  const write = out.write; // FileSystemWritableFileStream or null
  const chunks = write ? null : [];
  const segmentCount = video.segments.length + (audio ? audio.segments.length : 0);
  let bytes = 0;

  const emit = (buf) => {
    bytes += buf.length;
    if (write) return write.write(buf);
    chunks.push(buf);
    return Promise.resolve();
  };

  try {
    await emit(header);

    let done = 0;
    const tick = () => {
      done += 1;
      report("segments", {
        segmentIndex: done,
        segmentCount,
        percent: done / segmentCount,
        bytes,
      });
    };

    // Interleave video/audio fragments pairwise. Order across tracks is free —
    // every moof is self-describing (tfdt carries that track's media time) —
    // but pairing keeps both fetch streams moving and progress even.
    const n = Math.max(video.segments.length, audio ? audio.segments.length : 0);
    for (let i = 0; i < n; i += 1) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      if (video.segments[i]) {
        await emit(prepareFragment(await fetchRetry(video.segments[i], { as: "bytes", signal }), false));
        tick();
      }
      if (audio && audio.segments[i]) {
        await emit(prepareFragment(await fetchRetry(audio.segments[i], { as: "bytes", signal }), true));
        tick();
      }
    }

    report("finalizing", { percent: 1, segmentIndex: segmentCount, segmentCount, bytes });
    if (write) await write.close();
    else triggerBlobSave(new Blob(chunks, { type: "video/mp4" }), filename);
  } catch (e) {
    // A cancelled/failed download must not leave a half-open file handle.
    try {
      await write?.abort?.();
    } catch {}
    throw e;
  }

  return { filename, bytes, segmentCount };
}

/**
 * Default saver, best first:
 *   1. Stream through the service worker: the browser saves it to its own
 *      Downloads folder exactly like a file fetched off the internet — no
 *      prompt, and no multi-gigabyte Blob in memory.
 *   2. File System Access API (Chrome/Edge): streams to a location the user
 *      picks. Only reached when route 1 is refused.
 *   3. Collect chunks and save one Blob when the download ends. Memory-bound,
 *      so it is genuinely a last resort (Safari/Firefox, small files).
 */
async function defaultSavePicker(filename) {
  if (streamDownloadSupported()) {
    try {
      const stream = await openStreamDownload(filename, { contentType: "video/mp4" });
      return { write: stream };
    } catch (e) {
      if (!(e instanceof StreamDownloadUnavailable)) throw e;
      // Nothing picked the stream up (download blocked) — fall through.
    }
  }
  if (typeof window !== "undefined" && typeof window.showSaveFilePicker === "function") {
    const handle = await window.showSaveFilePicker({
      suggestedName: filename,
      types: [{ description: "MP4 video", accept: { "video/mp4": [".mp4"] } }],
    });
    return { write: await handle.createWritable() };
  }
  return { write: null };
}

function triggerBlobSave(blob, filename) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
}
